import React from 'react';

/** Sticky header — all th stay visible on vertical scroll */
const STICKY_HEADER_CLASSES =
  '[&_thead_th]:sticky [&_thead_th]:top-0 [&_thead_th]:z-20';

/** Sticky first column — stays visible on horizontal scroll; corner cell above both axes */
const STICKY_FIRST_COLUMN_CLASSES =
  '[&_thead_th:first-child]:left-0 [&_thead_th:first-child]:z-30 ' +
  '[&_tbody_td:first-child]:sticky [&_tbody_td:first-child]:left-0 [&_tbody_td:first-child]:z-10';

export function MobileScrollHint({ children = 'Swipe sideways to see more columns' }) {
  return (
    <p className="border-b border-slate-100 px-4 py-2 text-[11px] leading-snug text-slate-400 sm:hidden">
      {children}
    </p>
  );
}

/**
 * Container-scoped table scroll: vertical scroll when rows exceed viewport,
 * horizontal scroll when table is wider than the container.
 *
 * Defaults: sticky header + sticky first column (admin list UX).
 * Pass stickyFirstColumn={false} only for narrow single-identifier tables.
 */
export default function ScrollableTable({
  children,
  className = '',
  maxHeight,
  mobileMaxHeight = 'min(52dvh, 440px)',
  desktopMaxHeight = 'min(70vh, 720px)',
  stickyFirstColumn = true,
  stickyHeader = true,
  mobileScrollHint,
  ariaLabel = 'Scrollable data table',
}) {
  const heightStyle = maxHeight
    ? { maxHeight, '--scroll-table-max-h-mobile': maxHeight, '--scroll-table-max-h-desktop': maxHeight }
    : {
        '--scroll-table-max-h-mobile': mobileMaxHeight,
        '--scroll-table-max-h-desktop': desktopMaxHeight,
      };

  return (
    <>
      {mobileScrollHint ? <MobileScrollHint>{mobileScrollHint}</MobileScrollHint> : null}
      <div
        role="region"
        aria-label={ariaLabel}
        data-scroll-table=""
        className={[
          'scroll-table-viewport scroll-region max-w-full overflow-auto scroll-smooth',
          stickyFirstColumn ? 'scroll-table-sticky-col' : '',
          stickyHeader ? STICKY_HEADER_CLASSES : '',
          stickyFirstColumn ? STICKY_FIRST_COLUMN_CLASSES : '',
          className,
        ]
          .filter(Boolean)
          .join(' ')}
        style={heightStyle}
      >
        {children}
      </div>
    </>
  );
}

/**
 * Scrollable list body for divide-y row layouts. Keep section headers outside this wrapper.
 */
export function ScrollableListBody({
  children,
  className = '',
  maxHeight,
  mobileMaxHeight = 'min(48dvh, 400px)',
  desktopMaxHeight = 'min(60vh, 560px)',
  divideClassName = '',
  listStripe = true,
  ariaLabel = 'Scrollable list',
}) {
  const heightStyle = maxHeight
    ? { maxHeight, '--scroll-list-max-h-mobile': maxHeight, '--scroll-list-max-h-desktop': maxHeight }
    : {
        '--scroll-list-max-h-mobile': mobileMaxHeight,
        '--scroll-list-max-h-desktop': desktopMaxHeight,
      };

  return (
    <div
      role="region"
      aria-label={ariaLabel}
      {...(listStripe ? { 'data-list-stripe': '' } : {})}
      className={[
        'scroll-list-viewport scroll-region overflow-y-auto scroll-smooth',
        listStripe ? 'list-stripe-dividers' : divideClassName,
        className,
      ]
        .filter(Boolean)
        .join(' ')}
      style={heightStyle}
    >
      {children}
    </div>
  );
}
